import {
  Button,
  Heading,
  Icon,
  Modal,
  Select,
  Spinner,
  Text,
} from "@ttc3k/trekker";
import { Code, OpenNewWindow, Xmark } from "iconoir-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Flex, Grid, styled } from "styled-system/jsx";
import { CopyToClipboard } from "shared/components/molecules";
import {
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  useApiAccessByOrgQuery,
} from "gql/generated";
import { useAppContext } from "useAppContext";
import { useHelpScout } from "shared/hooks";
import { getMultilingualString } from "shared/utils";
import {
  getDeveloperZoneEndpoint,
  isAdminApiToken,
  isPrivateApiToken,
  isUserScopedApiToken,
} from "shared/utils/apiAccessUtils";
import { format } from "date-fns";
import { DeveloperZoneExamples } from "./DeveloperZoneExamples";
import { DeveloperZoneSandbox } from "./DeveloperZoneSandbox";

const DOCS_URL =
  "https://whereabouts.helpscoutdocs.com/category/114-getting-started";

const maskToken = (token: string) => {
  if (token.length <= 18) return token;
  return `${token.slice(0, 10)}...${token.slice(-8)}`;
};

type DeveloperZoneSlideoutProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const DeveloperZoneSlideout = ({
  isOpen,
  onClose,
}: DeveloperZoneSlideoutProps) => {
  const { t } = useTranslation("core", { keyPrefix: "DEVELOPER_ZONE" });
  const { t: tCore } = useTranslation("core");
  const { dbUser, organizations } = useAppContext();
  const { handleBeaconClick } = useHelpScout();

  const developerOrgIds = useMemo(() => {
    return new Set(
      (dbUser?.access?.organizations ?? [])
        .filter(
          o =>
            o?.role === EnumUserAccessOperatorsRole.Developer &&
            o?.status === EnumUserAccessOperatorsStatus.Accepted,
        )
        .map(o => o!.entityId as unknown as string),
    );
  }, [dbUser?.access?.organizations]);

  const developerOrganizations = useMemo(
    () => organizations.filter(o => developerOrgIds.has(o._id)),
    [organizations, developerOrgIds],
  );

  const { data, loading, error } = useApiAccessByOrgQuery({
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const allKeys = useMemo(
    () => (data?.apiAccessByOrg ?? []).filter(Boolean) as ApiAccessKey[],
    [data?.apiAccessByOrg],
  );

  const privateKeys = useMemo(
    () => allKeys.filter(key => isPrivateApiToken(key.token)),
    [allKeys],
  );

  const adminKeys = useMemo(
    () => allKeys.filter(key => isAdminApiToken(key.token)),
    [allKeys],
  );

  const keysByOrg = useMemo(() => {
    const map = new Map<string, ApiAccessKey[]>();
    allKeys
      .filter(key => !isUserScopedApiToken(key.token))
      .forEach(key => {
        if (!key.organization?._id) return;
        const orgId = key.organization._id as unknown as string;
        const list = map.get(orgId) ?? [];
        list.push(key);
        map.set(orgId, list);
      });
    return map;
  }, [allKeys]);

  const orgOptions = useMemo(
    () =>
      developerOrganizations.map(org => ({
        value: org._id,
        label: getMultilingualString(org.name) || org._id,
      })),
    [developerOrganizations],
  );

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [sandboxKey, setSandboxKey] = useState<ApiAccessKey | null>(null);

  useEffect(() => {
    if (!selectedOrgId && orgOptions.length > 0) {
      setSelectedOrgId(orgOptions[0].value);
    }
  }, [orgOptions, selectedOrgId]);

  const selectedOrgKeys = selectedOrgId
    ? (keysByOrg.get(selectedOrgId) ?? [])
    : [];
  const sampleKey =
    selectedOrgKeys[0]?.token ??
    adminKeys[0]?.token ??
    privateKeys[0]?.token ??
    "";
  const sampleOrganizationId =
    selectedOrgKeys[0]?.organization?._id ??
    adminKeys[0]?.organization?._id ??
    privateKeys[0]?.organization?._id ??
    selectedOrgId;
  const sampleEndpoint = sampleKey
    ? getDeveloperZoneEndpoint(sampleKey)
    : `${import.meta.env.VITE_API_SERVER_URL}/graphql/dev-partner`;
  const selectedOrgName =
    orgOptions.find(o => o.value === selectedOrgId)?.label ?? selectedOrgId;

  const hasOrgKeys = orgOptions.length > 0;
  const hasPrivateKeys = privateKeys.length > 0;
  const hasAdminKeys = adminKeys.length > 0;
  const showEmptyState =
    !loading && !error && !hasOrgKeys && !hasPrivateKeys && !hasAdminKeys;

  return (
    <>
      <Modal
        open={isOpen}
        placement="rightPane"
        onOpenChange={onClose}
        hideCloseTrigger={true}
        portal
      >
        <Grid
          gridTemplateRows={"auto 1fr"}
          h={"full"}
          gap={"0px"}
          gridGap={"0px"}
          gridRowGap={"0px"}
          overflow="hidden"
          bg="white"
        >
          {/* Header */}
          <Flex
            flexDirection={"column"}
            py={"600"}
            px={"500"}
            gap={"200"}
            borderBottomWidth={"1px"}
            borderBottomColor={"border.light"}
            borderBottomStyle={"solid"}
            position={"relative"}
          >
            <styled.button
              type="button"
              onClick={onClose}
              aria-label={tCore("CANCEL")}
              position="absolute"
              top="300"
              right="300"
              border="none"
              bg="transparent"
              cursor="pointer"
              p="100"
              borderRadius="full"
              color="icon.mid"
              _hover={{ bg: "bg.subtle", color: "icon.dark" }}
              _focusVisible={{
                outline: "1px solid",
                outlineColor: "border.light",
                outlineOffset: "2px",
              }}
            >
              <Icon Element={Xmark} size="md" />
            </styled.button>
            <Flex alignItems={"center"} gap={"150"}>
              <Icon Element={Code} />
              <Heading size={"h5"} color={"text.dark"}>
                {t("TITLE")}
              </Heading>
            </Flex>
            <Text color={"text.mid"}>{t("DESCRIPTION")}</Text>
          </Flex>

          {/* Body */}
          <Flex
            flexDirection={"column"}
            overflowY={"auto"}
            px={"500"}
            py={"400"}
            gap={"500"}
          >
            {/* Section: Docs link */}
            <Flex flexDir="column" gap="200">
              <Text visual="bodySemiBold" color="text.dark">
                {t("DOCS_TITLE")}
              </Text>
              <styled.a
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                display="inline-flex"
                alignItems="center"
                gap="100"
                color="text.accent.blue.mid"
                _hover={{ color: "text.accent.blue.dark" }}
              >
                <Text visual="body" color="inherit">
                  {t("DOCS_LINK")}
                </Text>
                <Icon Element={OpenNewWindow} size="sm" color="inherit" />
              </styled.a>
            </Flex>

            <Divider borderColor="border.light" />

            {/* Section: Admin API keys */}
            {hasAdminKeys && (
              <>
                <Flex flexDir="column" gap="200">
                  <Text visual="bodySemiBold" color="text.dark">
                    {t("ADMIN_KEYS_TITLE")}
                  </Text>
                  <Text color="text.light" fontSize="sm">
                    {t("ADMIN_KEYS_DESCRIPTION")}
                  </Text>
                  <Flex flexDir="column" gap="150">
                    {adminKeys.map(apiKey => (
                      <KeyCard
                        key={apiKey._id}
                        apiKey={apiKey}
                        onOpenSandbox={() => setSandboxKey(apiKey)}
                      />
                    ))}
                  </Flex>
                </Flex>
                {(hasPrivateKeys || hasOrgKeys) && (
                  <Divider borderColor="border.light" />
                )}
              </>
            )}

            {/* Section: Private API keys */}
            {hasPrivateKeys && (
              <>
                <Flex flexDir="column" gap="200">
                  <Text visual="bodySemiBold" color="text.dark">
                    {t("PRIVATE_KEYS_TITLE")}
                  </Text>
                  <Text color="text.light" fontSize="sm">
                    {t("PRIVATE_KEYS_DESCRIPTION")}
                  </Text>
                  <Flex flexDir="column" gap="150">
                    {privateKeys.map(apiKey => (
                      <KeyCard
                        key={apiKey._id}
                        apiKey={apiKey}
                        onOpenSandbox={() => setSandboxKey(apiKey)}
                      />
                    ))}
                  </Flex>
                </Flex>
                {hasOrgKeys && <Divider borderColor="border.light" />}
              </>
            )}

            {loading && (
              <Flex alignItems="center" gap="100">
                <Spinner w="16px" h="16px" />
                <Text color="text.light">{t("LOADING")}</Text>
              </Flex>
            )}
            {error && (
              <Text color="text.error.mid">{t("ERROR_LOADING")}</Text>
            )}
            {showEmptyState && (
              <Text color="text.light">{t("NO_ORGS")}</Text>
            )}

            {/* Section: Org / key selection */}
            {hasOrgKeys && (
            <Flex flexDir="column" gap="200">
              <Text visual="bodySemiBold" color="text.dark">
                {t("ORGS_TITLE")}
              </Text>
              {orgOptions.length > 1 && (
                <OrganizationSelect
                  orgOptions={orgOptions}
                  selectedOrgId={selectedOrgId}
                  onSelect={setSelectedOrgId}
                />
              )}
              {selectedOrgId && (
                <SelectedOrgPanel
                  organizationId={selectedOrgId}
                  organizationName={selectedOrgName}
                  keys={selectedOrgKeys}
                  onOpenSandbox={setSandboxKey}
                  onRequestKey={() => {
                    onClose();
                    setTimeout(
                      () =>
                        handleBeaconClick({
                          subject: `API key request for ${selectedOrgName}`,
                        }),
                      150,
                    );
                  }}
                />
              )}
            </Flex>
            )}

            {sampleKey ? (
              <>
                <Divider borderColor="border.light" />
                <Flex flexDir="column" gap="200">
                  <Text visual="bodySemiBold" color="text.dark">
                    {t("EXAMPLES_TITLE")}
                  </Text>
                  <DeveloperZoneExamples
                    endpoint={sampleEndpoint}
                    token={sampleKey}
                    organizationId={
                      sampleOrganizationId
                        ? String(sampleOrganizationId)
                        : undefined
                    }
                  />
                </Flex>
              </>
            ) : null}
          </Flex>
        </Grid>
      </Modal>
      <DeveloperZoneSandboxModal
        apiKey={sandboxKey}
        onClose={() => setSandboxKey(null)}
      />
    </>
  );
};

const OrganizationSelect = ({
  orgOptions,
  selectedOrgId,
  onSelect,
}: {
  orgOptions: Array<{ value: string; label: string }>;
  selectedOrgId: string;
  onSelect: (id: string) => void;
}) => {
  const { t } = useTranslation("core", { keyPrefix: "DEVELOPER_ZONE" });
  const collection = Select.createCollection({ items: orgOptions });
  return (
    <Select.Root
      collection={collection}
      value={selectedOrgId ? [selectedOrgId] : []}
      onValueChange={({ value }: { value: string[] }) =>
        onSelect(value[0] ?? "")
      }
      label={t("SELECT_ORG_LABEL")}
      placeholder={t("SELECT_ORG_PLACEHOLDER")}
    >
      {collection.items.map(item => (
        <Select.Item key={item.value} item={item}>
          {item.label}
        </Select.Item>
      ))}
    </Select.Root>
  );
};

type ApiAccessKey = {
  _id: string;
  token: string;
  name?: string | null;
  description?: string | null;
  lastUsedAt?: string | null;
  organization?: {
    _id: string;
    name?: {
      en?: string | null;
      es?: string | null;
      fr?: string | null;
    } | null;
  } | null;
};

const SelectedOrgPanel = ({
  organizationId,
  organizationName,
  keys,
  onOpenSandbox,
  onRequestKey,
}: {
  organizationId: string;
  organizationName: string;
  keys: Array<ApiAccessKey | null>;
  onOpenSandbox: (apiKey: ApiAccessKey) => void;
  onRequestKey: () => void;
}) => {
  const { t } = useTranslation("core", { keyPrefix: "DEVELOPER_ZONE" });
  const validKeys = keys.filter(Boolean) as ApiAccessKey[];

  return (
    <Flex flexDir="column" gap="200">
      <Flex flexDir="column" gap="50">
        <Text visual="bodyMedium" color="text.dark">
          {organizationName}
        </Text>
        <Flex alignItems="center" gap="100">
          <Text fontFamily="mono" fontSize="xs" color="text.light">
            {organizationId}
          </Text>
          <CopyToClipboard
            iconOnly
            text={organizationId}
            iconCopyColor="icon.light"
            iconSize="sm"
          />
        </Flex>
      </Flex>
      {validKeys.length === 0 ? (
        <Flex
          flexDir="column"
          gap="150"
          p="250"
          borderRadius="100"
          bg="bg.subtlest"
          border="1px solid"
          borderColor="border.light"
        >
          <Text color="text.mid" lineHeight="snug">
            {t("NO_KEYS")}
          </Text>
          <Button
            visual="primary"
            mode="light"
            size="sm"
            alignSelf="flex-start"
            onClick={onRequestKey}
          >
            {t("REQUEST_KEY_CTA")}
          </Button>
        </Flex>
      ) : (
        <Flex flexDir="column" gap="150">
          {validKeys.map(apiKey => (
            <KeyCard
              key={apiKey._id}
              apiKey={apiKey}
              onOpenSandbox={() => onOpenSandbox(apiKey)}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const KeyCard = ({
  apiKey,
  onOpenSandbox,
}: {
  apiKey: ApiAccessKey;
  onOpenSandbox: () => void;
}) => {
  const { t } = useTranslation("core", { keyPrefix: "DEVELOPER_ZONE" });
  const lastUsedDisplay = apiKey.lastUsedAt
    ? format(new Date(apiKey.lastUsedAt), "MMM d, yyyy h:mm a")
    : t("NEVER");
  return (
    <Flex
      flexDir="column"
      gap="100"
      p="200"
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
      bg="bg.subtlest"
    >
      {(apiKey.name || apiKey.description) && (
        <Flex flexDir="column" gap="25">
          {apiKey.name && (
            <Text visual="bodySemiBold" color="text.dark">
              {apiKey.name}
            </Text>
          )}
          {apiKey.description && (
            <Text color="text.mid" fontSize="sm">
              {apiKey.description}
            </Text>
          )}
        </Flex>
      )}
      <Flex alignItems="center" gap="100">
        <Text
          fontFamily="mono"
          fontSize="xs"
          color="text.mid"
          flex={1}
          wordBreak="break-all"
        >
          {maskToken(apiKey.token)}
        </Text>
        <CopyToClipboard
          iconOnly
          text={apiKey.token}
          iconCopyColor="icon.mid"
        />
      </Flex>
      <Button
        visual="primary"
        mode="light"
        size="sm"
        alignSelf="flex-start"
        onClick={onOpenSandbox}
      >
        {t("OPEN_SANDBOX")}
      </Button>
      <Text color="text.light" fontSize="xs">
        {t("LAST_USED")}: {lastUsedDisplay}
      </Text>
    </Flex>
  );
};

const DeveloperZoneSandboxModal = ({
  apiKey,
  onClose,
}: {
  apiKey: ApiAccessKey | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation("core", { keyPrefix: "DEVELOPER_ZONE" });
  const { t: tCore } = useTranslation("core");
  if (!apiKey) return null;

  const endpoint = getDeveloperZoneEndpoint(apiKey.token);
  const organizationId = apiKey.organization?._id
    ? String(apiKey.organization._id)
    : undefined;
  const organizationName = apiKey.organization?.name
    ? getMultilingualString(apiKey.organization.name)
    : isAdminApiToken(apiKey.token)
      ? t("ADMIN_KEYS_TITLE")
      : t("PRIVATE_KEYS_TITLE");

  return (
    <Modal
      open
      onOpenChange={onClose}
      placement="centerPane"
      closeTriggerVisual="light"
      hideCloseTrigger
      noMaxH
      portal
    >
      <Flex
        width="calc(100vw - 64px)"
        height="calc(100vh - 64px)"
        borderRadius="200"
        overflow="hidden"
        bg="white"
        flexDir="column"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          width="full"
          py="200"
          px="300"
          bg="bg.wa.light"
          borderBottomWidth="1px"
          borderBottomColor="border.light"
          borderBottomStyle="solid"
          flexShrink={0}
        >
          <Flex flexDir="column" gap="25" minW={0}>
            <Flex alignItems="center" gap="150">
              <Icon Element={Code} size="sm" />
              <Heading size="h5" color="text.dark">
                {t("SANDBOX_TITLE")}
              </Heading>
            </Flex>
            <Text color="text.light" fontSize="sm">
              {organizationName} · {apiKey.name || maskToken(apiKey.token)}
            </Text>
          </Flex>
          <Button mode="light" onClick={onClose} type="button">
            {tCore("CLOSE")}
          </Button>
        </Flex>

        <Flex flex={1} overflow="hidden" h={0}>
          <DeveloperZoneSandbox
            endpoint={endpoint}
            token={apiKey.token}
            organizationId={organizationId}
            height="100%"
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
